import React, { useMemo, useState } from "react"
import {
  Box, Button, Flex,
} from "@chakra-ui/react"
import Menu, { MenuItem } from "components/dialogs/Menu"
import { successToast, errorToast } from "utilities/toasts"
import { useHistory } from "react-router-dom"
import { useQueryClient } from "react-query"
import { useShallow } from "zustand/shallow"
import Icon from "components/elements/Icon"
import SegmentIcon from "components/elements/icons/SegmentIcon"
import UpgradeGateModal from "components/elements/UpgradeGateModal"
import {
  Filter, EventGroup, Segment, SegmentRuleOperator,
} from "sharedTypes"
import useGuestsStore from "services/useGuestsStore"
import { postSegment, putSegment } from "api/guests"
import { addSegment, updateSegment } from "context/actions"
import { useEventGuestPaths } from "utilities/routes"
import { serializeFilters, useUrlFilters } from "utilities/filterRulesUrl"
import {
  chipToRule, chipHasValue, ruleValueToChipValues, generateChipId, chipsEqual, isSegmentDirty,
} from "utilities/chipRules"
import useFeatureFlag from "services/featureFlags"
import { withSectionBoundary } from "components/ErrorBoundary"
import {
  computeDisabledFields,
  FILTER_FIELDS,
} from "./config"
import { normalizeOperator } from "./operatorLogic"
import { FilterFieldConfig } from "./types"
import useFilterOptionsContext from "./useFilterOptionsContext"
import FilterChip from "./FilterChip"
import AddFilterPopover from "./AddFilterPopover"
import SaveSegmentPopover from "./SaveSegmentPopover"
import getDefaultGroupChips from "./defaultGroupChips"

type Props = {
  eventId: string
  segments: Segment[]
  eventGroups: EventGroup[]
  segmentsEnabled: boolean
}

const GuestFilters = ({
  eventId, segments, eventGroups, segmentsEnabled,
}: Props) => {
  const history = useHistory()
  const queryClient = useQueryClient()
  const { eventGuestsPath } = useEventGuestPaths()
  const {
    optionsContext, getConfig, fetchMoreQuestions,
  } = useFilterOptionsContext(eventId, eventGroups)
  const featureFlags = useFeatureFlag()

  // Feature-flag filtered static fields. The "Form Questions" sentinel is
  // included here — it shows as a single category entry in the picker and
  // opens a question sub-picker (step 2 in AddFilterPopover).
  const activeFilterFields: FilterFieldConfig[] = useMemo(
    () => FILTER_FIELDS.filter((f) => !f.featureFlag || featureFlags[f.featureFlag]),
    [featureFlags],
  )

  const {
    segmentId, groupId, searchString,
  } = useGuestsStore(
    useShallow((s) => ({
      segmentId: s.segmentId,
      groupId: s.groupId,
      searchString: s.searchString,
    })),
  )
  const filters = useUrlFilters()

  // Split-button state for the dirty-custom-segment case. Two independent
  // open states: the caret's dropdown menu, and the "save as new segment"
  // popover that fires from one of its items.
  const [saveAsMenuOpen, setSaveAsMenuOpen] = useState(false)
  const [saveAsNewOpen, setSaveAsNewOpen] = useState(false)

  // Chips are derived from one of three sources, in this priority:
  //   1. store.filters — present after the user has mutated chips. URL has
  //      `?rules=`, store mirrors it.
  //   2. The active custom segment's rules — when the user picked a saved
  //      segment from the sidebar and hasn't modified anything yet.
  //   3. The active default-group preset — when the user picked Confirmed /
  //      Checked In / etc. The preset is the chip equivalent of what the
  //      backend's virtual group does, so the chip row reads as a faithful
  //      representation of the active view.
  const defaultGroupChips = useMemo(() => getDefaultGroupChips(groupId), [groupId])

  const chips: Filter[] = useMemo(() => {
    if (filters.length > 0) return filters
    if (segmentId) {
      const segment = segments.find((s) => s.id === segmentId)
      if (segment) {
        return segment.rules.map((rule, i) => ({
          id: `segment-${i}`,
          field: rule.field,
          operator: rule.operator,
          // Chip.values is always string[] — numeric fields render the
          // value via DatePicker / NumberInput which both accept strings.
          // Stringify here so chips behave uniformly. Filter null/undefined
          // entries first so we never render the literal text "null" — can
          // happen with legacy segments saved against the old strong-params
          // bug that dropped array `value`.
          values: ruleValueToChipValues(rule.value),
        }))
      }
    }
    if (defaultGroupChips) return defaultGroupChips

    return []
  }, [filters, segmentId, segments, defaultGroupChips])

  const activeSegment = useMemo(
    () => (segmentId ? segments.find((s) => s.id === segmentId) : undefined),
    [segmentId, segments],
  )
  // "Dirty" means the visible chips diverge from the active identity's
  // expected chip set. Empty `filters` is never dirty — chips are showing
  // the identity verbatim. Custom segments diverge from `segment.rules`;
  // default groups diverge from their preset.
  const isDirty = useMemo(() => {
    if (filters.length === 0) return false
    if (activeSegment) return isSegmentDirty(activeSegment, filters)
    if (defaultGroupChips) return !chipsEqual(defaultGroupChips, filters)

    return false
  }, [filters, activeSegment, defaultGroupChips])
  // The save buttons appear when there's something worth persisting: chips
  // exist without a backing custom segment (POST new), or they diverge from
  // an active identity (PUT for custom segments, POST new for default
  // groups since you can't "save changes" to a built-in preset).
  const canSave = (filters.length > 0 && !activeSegment) || isDirty

  // Pushes the current chip set to the URL. When a saved segment OR a
  // chippable default group is active, its identity marker (`segmentId=` /
  // `groupId=`) stays alongside `rules=` so the sidebar selection sticks
  // and the title knows what to diff against for the dirty asterisk.
  // Empty chips drop everything so the user lands on All Guests rather
  // than bouncing back to the active identity via hydration. Active search
  // is preserved — filters and search are orthogonal axes that coexist.
  const pushRules = (next: Filter[]) => {
    const parts: string[] = []
    if (next.length > 0) {
      if (segmentId) parts.push(`segmentId=${segmentId}`)
      if (groupId && defaultGroupChips) parts.push(`groupId=${groupId}`)
      parts.push(`rules=${serializeFilters(next)}`)
    }
    if (searchString) parts.push(`q=${encodeURIComponent(searchString)}`)
    history.push(eventGuestsPath(eventId, parts.join("&")))
  }

  const addFilter = (field: string, values: string[], operatorOverride?: SegmentRuleOperator) => {
    const config = getConfig(field)
    if (!config) return
    const newChip: Filter = {
      id: generateChipId(),
      field,
      operator: operatorOverride
        ?? normalizeOperator(config.defaultOperator, values.length, !!config.multiSelect),
      values,
    }
    pushRules([...chips, newChip])
  }

  const updateFilter = (id: string, patch: Partial<Filter>) => {
    pushRules(chips.map((f) => {
      if (f.id !== id) return f
      const merged = { ...f, ...patch }
      const config = getConfig(merged.field)
      if (!config) return merged
      // If the patch touched `values`, re-derive the wire-level operator so
      // it stays in sync with the count (scalar eq ↔ array in, ne ↔ not_in).
      // Patches that only touch `operator` skip this — the user explicitly
      // picked it.
      const operator = patch.values
        ? normalizeOperator(merged.operator, merged.values.length, !!config.multiSelect)
        : merged.operator

      return { ...merged, operator }
    }))
  }

  const removeFilter = (id: string) => {
    pushRules(chips.filter((f) => f.id !== id))
  }

  const saveAsNewSegment = async (name: string, color: string) => {
    const rules = chips.filter(chipHasValue).map(chipToRule)
    try {
      const { data } = await postSegment(eventId, { name, color, rules })
      addSegment(queryClient, eventId, data)
      const parts = [`segmentId=${data.id}`]
      if (searchString) parts.push(`q=${encodeURIComponent(searchString)}`)
      history.push(eventGuestsPath(eventId, parts.join("&")))
      successToast({
        title: "Segment saved",
        description: `"${name}" is now in your segments.`,
      })
    } catch (err) {
      errorToast({ title: "Couldn't save segment" })
      throw err
    }
  }

  const saveChangesToSegment = async () => {
    if (!activeSegment) return
    const rules = chips.filter(chipHasValue).map(chipToRule)
    try {
      const { data } = await putSegment(eventId, {
        ...activeSegment,
        rules,
      })
      updateSegment(queryClient, eventId, data)
      // segment.rules isn't in the cache key, so an in-place edit won't
      // bump it — invalidate so the table refetches against the new rules.
      queryClient.invalidateQueries(["guests", eventId])
      const parts = [`segmentId=${activeSegment.id}`]
      if (searchString) parts.push(`q=${encodeURIComponent(searchString)}`)
      history.push(eventGuestsPath(eventId, parts.join("&")))
      successToast({
        title: "Segment updated",
        description: `"${activeSegment.name}" has been updated.`,
      })
    } catch {
      errorToast({ title: "Couldn't update segment" })
    }
  }

  // Any field can repeat — the BE chains rules with AND, so two
  // identical chips dedupe naturally and contradictory ones just return
  // an empty set the user can self-correct. The only disable case left
  // is fields whose dynamic option source is empty (nothing to pick).
  const disabledFields = useMemo(
    () => computeDisabledFields(activeFilterFields, optionsContext),
    [activeFilterFields, optionsContext],
  )

  // Save area sits outside the wrap so chips can fill — and overflow into a
  // second row — without ever pushing the Save button below them.
  const saveArea = canSave && (() => {
    // Free plan: any save attempt routes to the upgrade modal — the
    // dirty/save-changes branching only matters on paid plans where
    // the segments backend is actually writable.
    if (!segmentsEnabled) {
      return (
        <UpgradeGateModal
          icon={SegmentIcon}
          title="Save Segment"
          description="Upgrade to save segments for future use and share them across your team."
        >
          <Button
            variant="ghost"
            colorScheme="blue"
            leftIcon={<SegmentIcon boxSize={3} color="blue.500" />}
          >
            Save Segment
          </Button>
        </UpgradeGateModal>
      )
    }
    // Dirty + active custom segment → dropdown Menu button (same shape
    // as the `View` button in the header). Items: primary `Save changes`
    // (PUT) and `Save as new segment` (POST via the SaveSegmentPopover,
    // controlled mode, anchored to the Menu's Box wrapper).
    if (isDirty && activeSegment) {
      return (
        <SaveSegmentPopover
          initialColor={activeSegment.color ?? "blue"}
          onSave={saveAsNewSegment}
          controlledOpen={saveAsNewOpen}
          onControlledClose={() => setSaveAsNewOpen(false)}
        >
          <Box display="inline-block">
            <Menu
              menuItems={[
                <MenuItem key="save-changes" onClick={saveChangesToSegment}>
                  Save changes
                </MenuItem>,
                <MenuItem
                  key="save-as-new"
                  onClick={() => setSaveAsNewOpen(true)}
                >
                  Save as new segment
                </MenuItem>,
              ]}
              isOpen={saveAsMenuOpen}
              onOpen={() => setSaveAsMenuOpen(true)}
              onClose={() => setSaveAsMenuOpen(false)}
              placement="bottom-end"
              closeOnSelect
              button={(
                <Button
                      // Match the in-house Menu's `buttonText` path:
                      // `pointerEvents="auto"` lets the inner Button receive
                      // hover / active styles inside the nested-button DOM,
                      // and an explicit `onClick` is needed because with
                      // pointerEvents enabled the click stops at the inner
                      // Button rather than bubbling to MenuButton.
                  pointerEvents="auto"
                  onClick={() => setSaveAsMenuOpen(true)}
                  variant="ghost"
                  colorScheme="blue"
                  leftIcon={<SegmentIcon boxSize={3} color="blue.500" />}
                  rightIcon={<Icon icon="down-arrow" size={3} />}
                >
                  Save changes
                </Button>
                  )}
            />
          </Box>
        </SaveSegmentPopover>
      )
    }
    // No active segment → POST a new one via the SaveSegmentPopover
    // (asks for name + colour). Wording is intentionally "Save as
    // segment" — at this moment the chips are a transient filter, not
    // yet a named segment; "as" frames the action as a conversion.

    return (
      <SaveSegmentPopover
        initialColor={activeSegment?.color ?? "blue"}
        onSave={saveAsNewSegment}
      >
        <Button
          variant="ghost"
          colorScheme="blue"
          leftIcon={<SegmentIcon boxSize={3} color="blue.500" />}
        >
          Save Segment
        </Button>
      </SaveSegmentPopover>
    )
  })()

  return (
    // Chips wrap freely; Filter trigger + Save button live in the wrap so
    // they sit at the end of the last chip row. Parent HStack with
    // `alignItems="center"` aligns this whole flex with the searchbar.
    <Flex
      flex={1}
      minW={0}
      wrap="wrap"
      gap={2}
      align="center"
      data-testid="guest-filters"
    >
      {chips.map((filter) => {
        const config = getConfig(filter.field)
        if (!config) return null

        return (
          <Box key={filter.id} flexShrink={0}>
            <FilterChip
              filter={filter}
              config={config}
              context={optionsContext}
              onChange={(patch) => updateFilter(filter.id, patch)}
              onRemove={() => removeFilter(filter.id)}
            />
          </Box>
        )
      })}

      <AddFilterPopover
        fieldConfigs={activeFilterFields}
        context={optionsContext}
        disabledFields={disabledFields}
        onAdd={addFilter}
        onQuestionsScrollEnd={fetchMoreQuestions}
      >
        <Button
          variant="ghost"
          colorScheme="blue"
          leftIcon={<Icon icon="plus" size={3} color="blue.500" />}
        >
          Filter
        </Button>
      </AddFilterPopover>

      {saveArea}
    </Flex>
  )
}

export default withSectionBoundary(GuestFilters, { name: "guest-chip-filters" })
